import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doorService } from '../services';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import useDoorScanner from '../hooks/useDoorScanner';
import DoorScannerCard, { btnDark } from '../components/door/DoorScannerCard';
import DoorRosterSheet from '../components/door/DoorRosterSheet';
import { SCOPES, TONES } from '../components/door/doorKinds';
import { whenLabel } from '../utils/surfaceFormat';
import '../components/door/door.css';

export { isIosDevice } from '../hooks/useDoorScanner';

/**
 * DoorScanPage — the full door (design_handoff_door_scanner). One check-in
 * screen for everything scanned at a door, driven by `kind`; today only event
 * doors have endpoints, so the route mounts `kind="event"`.
 *
 * Title row, three stat tiles that open the list behind them, the black scanner
 * card (camera fades into the rainbow square; results flash over it while it
 * keeps scanning), manual code, and this session's log. The engine — rear-camera
 * ladder, iOS LiveTicketScanner, blank-frame watchdog, photo decode, 3s
 * duplicate debounce — lives in `useDoorScanner`, shared with the DOOR tab.
 */

const CARD = { background: '#fff', padding: 20, borderRadius: 28, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 };
const CARD_TITLE = { fontSize: 11, fontWeight: 700, letterSpacing: '.16em' };
const EMPTY = {
  padding: 20, borderRadius: 18, background: '#F5F5F5', textAlign: 'center',
  fontSize: 11, fontWeight: 700, letterSpacing: '.14em', color: '#9CA3AF',
};

export default function DoorScanPage({ kind = 'event' }) {
  const { id: eventId } = useParams();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [notAllowed, setNotAllowed] = useState(false);
  const [manualCode, setManualCode] = useState('');
  // Which list the sheet shows ('in' | 'sold' | 'left'), or null when closed.
  const [rosterScope, setRosterScope] = useState(null);
  const [rosterRows, setRosterRows] = useState([]);
  const [rosterTotal, setRosterTotal] = useState(0);
  const [rosterHasMore, setRosterHasMore] = useState(false);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [busyId, setBusyId] = useState(null);

  // The open roster, reachable from the scan handler — html5-qrcode captures its
  // decode callback once when the camera starts, so a scan reaches the list
  // that's on screen now through these.
  const rosterRowsRef = useRef([]);
  const refreshRosterRef = useRef(null);

  const loadSummary = useCallback(async () => {
    const r = await doorService.summary(eventId);
    if (r.success) setSummary(r.data);
    else if (r.status === 403 || /forbidden/i.test(r.error || '')) setNotAllowed(true);
  }, [eventId]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Refresh counts (cheap) after a check-in, and the open list with them so the
  // tiles and the names never disagree.
  const onResult = useCallback((r) => {
    if (r.result !== 'ok') return;
    loadSummary();
    refreshRosterRef.current?.(r);
  }, [loadSummary]);

  const scanner = useDoorScanner({ eventId, kind, onResult });
  const k = scanner.kind;

  // This page exists to scan. On iOS open the camera on arrival rather than
  // making the operator tap through to it.
  useEffect(() => {
    if (!scanner.nativeRes) return;
    scanner.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanner.nativeRes]);

  const openRoster = (scope) => { setRosterScope(scope); setSearch(''); };
  const closeRoster = useCallback(() => setRosterScope(null), []);

  // Typing filters server-side; 250ms is enough to not fire on every letter.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadRoster = useCallback(async (append = false) => {
    if (!rosterScope) return;
    setRosterLoading(true);
    const r = await doorService.roster(eventId, {
      scope: rosterScope,
      q: debouncedSearch,
      skip: append ? rosterRowsRef.current.length : 0,
      take: 25,
    });
    setRosterLoading(false);
    if (!r.success || !r.data) return;
    setRosterRows((prev) => {
      const next = append ? [...prev, ...(r.data.rows || [])] : (r.data.rows || []);
      rosterRowsRef.current = next;
      return next;
    });
    setRosterTotal(r.data.total || 0);
    setRosterHasMore(!!r.data.hasMore);
  }, [eventId, rosterScope, debouncedSearch]);

  // Reload from the top whenever the scope or the query changes.
  useEffect(() => {
    if (!rosterScope) {
      rosterRowsRef.current = [];
      setRosterRows([]); setRosterTotal(0); setRosterHasMore(false);
      return;
    }
    loadRoster(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterScope, debouncedSearch]);

  // A scan changes the list you're looking at, so the list follows it:
  //   1. Patch the scanned party's row from the scan result — instant, and it
  //      holds however far down you've scrolled.
  //   2. Refetch, because a scan also moves people between scopes and shifts
  //      every total. The refetch tops up as much as you'd loaded; the rest is
  //      left alone rather than yanked out from under your scroll.
  const refreshRosterAfterScan = useCallback(async (scan) => {
    if (!rosterScope) return;

    if (scan?.applicationId) {
      setRosterRows((prev) => {
        const next = prev.map((row) => (row.applicationId === scan.applicationId
          ? {
            ...row,
            checkedInCount: scan.checkedInCount ?? row.checkedInCount,
            checkedInAt: scan.checkedInAt ?? row.checkedInAt ?? new Date().toISOString(),
          }
          : row));
        rosterRowsRef.current = next;
        return next;
      });
    }

    const loaded = rosterRowsRef.current.length;
    const r = await doorService.roster(eventId, {
      scope: rosterScope,
      q: debouncedSearch,
      skip: 0,
      take: Math.min(100, Math.max(25, loaded)),
    });
    if (!r.success || !r.data) return;
    const fresh = r.data.rows || [];
    setRosterRows((prev) => {
      const seen = new Set(fresh.map((row) => row.applicationId));
      const tail = prev.slice(fresh.length).filter((row) => !seen.has(row.applicationId));
      const next = [...fresh, ...tail];
      rosterRowsRef.current = next;
      return next;
    });
    setRosterTotal(r.data.total || 0);
    setRosterHasMore(!!r.data.hasMore || rosterRowsRef.current.length > fresh.length);
  }, [eventId, rosterScope, debouncedSearch]);

  useEffect(() => { refreshRosterRef.current = refreshRosterAfterScan; }, [refreshRosterAfterScan]);

  const rosterSentinel = useInfiniteScroll(
    () => loadRoster(true),
    { hasMore: rosterHasMore, loading: rosterLoading },
  );

  const handleManual = async (e) => {
    e.preventDefault();
    const value = manualCode;
    setManualCode('');
    await scanner.submitManual(value);
  };

  const handleCheckIn = async (row) => {
    setBusyId(row.applicationId);
    await scanner.checkIn(row.applicationId);
    setBusyId(null);
  };

  const back = () => navigate(`/host/events/${eventId}`);

  const TopBar = (
    <header style={{ position: 'sticky', top: 0, zIndex: 30, background: '#fff', borderBottom: '2px solid #000', paddingTop: 'env(safe-area-inset-top)' }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', height: 64, padding: '0 20px', display: 'flex', alignItems: 'center', gap: 14, boxSizing: 'border-box' }}>
        <button
          type="button"
          onClick={back}
          className="door-back"
          style={{ display: 'flex', alignItems: 'center', gap: 8, height: 40, padding: '0 16px 0 12px', border: 'none', borderRadius: 999, background: '#F2F2F2', color: '#000', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
          BACK
        </button>
        <span style={{ flex: 1 }} />
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: '#6B7280' }}>
          <span className="door-pulse" aria-hidden="true" />OFFLINE-READY
        </span>
      </div>
    </header>
  );

  if (notAllowed) {
    return (
      <div className="door-root" style={{ minHeight: '100vh', background: '#F5F5F5', color: '#000' }}>
        {TopBar}
        <main style={{ maxWidth: 560, margin: '0 auto', padding: '24px 20px' }}>
          <section style={{ ...CARD, gap: 16 }}>
            <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.16em' }}>THIS DOOR ISN’T YOURS.</span>
            <span style={{ fontSize: 12, lineHeight: 1.6, color: '#6B7280' }}>
              You’re not the host or a co-host of this event, so you can’t run its door.
            </span>
            <button type="button" onClick={() => navigate('/')} style={{ ...btnDark, background: '#000', height: 48 }}>HOME</button>
          </section>
        </main>
      </div>
    );
  }

  const inN = summary?.ticketsCheckedIn;
  const sold = summary?.ticketsSold;
  const left = summary?.ticketsRemaining;
  const nums = [inN, sold, left];
  const when = summary?.eventStartsAt ? whenLabel(summary.eventStartsAt) : '';
  const city = (summary?.eventCity || '').split(',')[0].trim().toUpperCase();
  const meta = [when, city].filter(Boolean).join(' · ');

  return (
    <div className="door-root" style={{ minHeight: '100vh', background: '#F5F5F5', color: '#000' }}>
      {TopBar}

      <main style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 20px 48px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
            <span style={{ alignSelf: 'flex-start', padding: '6px 12px', borderRadius: 999, background: k.bg, color: '#000', fontSize: 10, fontWeight: 700, letterSpacing: '.16em' }}>
              DOOR · {k.label}
            </span>
            <h1 style={{ margin: 0, fontSize: 26, lineHeight: 1.2, fontWeight: 700, textWrap: 'pretty', overflowWrap: 'anywhere' }}>
              {summary?.eventTitle || ' '}
            </h1>
            {meta && <span style={{ fontSize: 11, letterSpacing: '.14em', color: '#6B7280' }}>{meta}</span>}
          </div>
          <button
            type="button"
            onClick={back}
            style={{ border: 'none', background: 'none', padding: 0, color: '#000', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textDecoration: 'underline', cursor: 'pointer' }}
          >
            MANAGE
          </button>
        </div>

        {/* Live counts — each one opens the list behind it. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8 }}>
          {SCOPES.map((scope, i) => (
            <button
              key={scope}
              type="button"
              className="door-tile"
              onClick={() => openRoster(scope)}
              aria-pressed={rosterScope === scope}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '16px 8px', border: 'none', borderRadius: 22, background: '#fff', fontFamily: 'inherit', cursor: 'pointer', minWidth: 0 }}
            >
              <span style={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{nums[i] ?? '—'}</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', opacity: 0.6 }}>{k.stats[i]}</span>
            </button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: 18, alignItems: 'start' }}>
          <DoorScannerCard
            scanner={scanner}
            counter={`${inN ?? 0} OF ${sold ?? 0} ${k.counted}`}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button type="button" className="door-btn-dark" style={btnDark} onClick={scanner.pickPhoto} disabled={scanner.readingPhoto}>
                {scanner.readingPhoto ? 'READING…' : 'PHOTO'}
              </button>
              <button type="button" className="door-btn-dark" style={btnDark} onClick={() => openRoster('left')}>BY NAME</button>
            </div>
          </DoorScannerCard>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
            <section style={CARD}>
              <label htmlFor="door-manual" style={CARD_TITLE}>MANUAL CODE / URL</label>
              <form onSubmit={handleManual} style={{ display: 'flex', gap: 8, margin: 0 }}>
                <input
                  id="door-manual"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  placeholder="Code or URL"
                  autoComplete="off"
                  autoCapitalize="off"
                  style={{ flex: 1, minWidth: 0, height: 48, padding: '0 18px', border: 'none', borderRadius: 999, background: '#F2F2F2', fontFamily: 'inherit', fontSize: 12, letterSpacing: '.04em', boxSizing: 'border-box' }}
                />
                <button type="submit" style={{ height: 48, padding: '0 22px', border: 'none', borderRadius: 999, background: '#000', color: '#fff', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.16em', cursor: 'pointer', flexShrink: 0 }}>
                  CHECK
                </button>
              </form>
            </section>

            <section style={CARD} data-testid="door-session">
              <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={CARD_TITLE}>THIS SESSION</span>
                {scanner.log.length > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: '#6B7280' }}>
                    {scanner.log.length} {scanner.log.length === 1 ? 'SCAN' : 'SCANS'}
                  </span>
                )}
              </span>
              {scanner.log.length === 0 ? (
                <span style={EMPTY}>NOBODY SCANNED YET.</span>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {scanner.log.map((row) => {
                    const t = TONES[row.tone];
                    return (
                      <div key={row.id} className="door-rise" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px 8px 8px', borderRadius: 18, background: '#F5F5F5' }}>
                        <span style={{ width: 44, height: 28, flexShrink: 0, borderRadius: 999, background: t.bg, color: t.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, letterSpacing: '.12em' }}>
                          {t.tag}
                        </span>
                        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{row.name}</span>
                          <span style={{ fontSize: 10, letterSpacing: '.1em', color: '#6B7280' }}>{row.meta}</span>
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em', color: '#6B7280', flexShrink: 0 }}>{row.time}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>

      {rosterScope && (
        <DoorRosterSheet
          kind={k}
          scope={rosterScope}
          onScope={(s) => setRosterScope(s)}
          onClose={closeRoster}
          total={rosterTotal}
          rows={rosterRows}
          search={search}
          onSearch={setSearch}
          loading={rosterLoading}
          hasMore={rosterHasMore}
          sentinelRef={rosterSentinel}
          onCheckIn={handleCheckIn}
          busyId={busyId}
          onOpenGuest={(g) => navigate(g.handle ? `/${g.handle}` : `/users/${g.guestUserId}`)}
        />
      )}
    </div>
  );
}
